import React from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { AxiosError } from 'axios';

import excursionAPI from 'api/excursionApi';
import { ExcursionPrepared, ExcursionType } from 'api/types';
import { PaymentState } from './useExcursion/state';
import { AuthError, NotFoundError } from 'errors';
import { StepType } from 'pages/Excursion';

import { useStream } from './useStream';

import placeholder from './../images/plane_background.png';

const useExcursion = () => {

    const [cookies] = useCookies();
    const { uid, sid } = cookies;
    const { t } = useTranslation();
    const { startViewStream } = useStream();


    const getExcursionInfo = React.useCallback(async (tid: number) => {
        const res = await excursionAPI.getExcursion(uid, sid, tid)
            .then(res => res.data)
            .catch((error) => {
                let errorMessage = t('error');

                if (error.response.data.status) {
                    switch (error.response.data.status) {
                        case 'user not found':
                            throw new AuthError();
                    }
                }
                throw new Error(errorMessage);
            });

        if (!res || !res.excursions) {
            throw new NotFoundError();
        }

        const [excursion] = res.excursions;

        return excursion;
    }, [cookies]);


    const joinMe = React.useCallback(async (
        tid: number,
        callback: (status: 'ok' | 'error', message: string) => void
    ) => {
        const ex = await excursionAPI
            .join(uid, sid, tid)
            .then(res => res.data)
            .catch((err: AxiosError) => {
                return err.response?.data
            });

        switch (ex.status) {
            case 'ok':
            case 'already_join':
                callback('ok', 'Присоединились успешно');
                break;
            case 'forbidden':
                callback('error', t('no access'));
                break;
            case 'limit_reached':
                callback('error', t('no places'));
                break;
            default:
                callback('error', t('error'))
        }
    }, [cookies]);


    const startStream = React.useCallback((
        excursion: ExcursionType,
        setCurrentScreen: (step: StepType) => void,
        setLoaderMessage: (msg: string) => void
    ) => {
        const { ex_status, ex_h_cost, ex_grp_fix_prepay, ex_tid } = excursion;
        const { isPayed } = PaymentState.getPaymentState();

        switch (ex_status) {
            case 'payed':
                setCurrentScreen('payment');
                break;
            case 'in_progress':
            case 'grp_wait_start_by_user':
                if (ex_h_cost === 0 || isPayed) {
                    startViewStream(excursion, setCurrentScreen, setLoaderMessage);
                } else {
                    if (ex_grp_fix_prepay) {
                        startViewStream(excursion, setCurrentScreen, setLoaderMessage);
                    } else {
                        setCurrentScreen('payment');
                    }
                }
                break;
            case 'grp_wait_confirmation_by_guide':
                toast('Гид еще не начал стрим, попробуйте позже');
                break;
            case 'grp_new':
                handleGrpNew(ex_tid);
                break;
            case 'grp_accepted_by_user':
                startViewStream(excursion, setCurrentScreen, setLoaderMessage);
                break;
        }
    }, []);

    const handleGrpNew = React.useCallback(async (
        ex_tid: number
    ) => {

        const patchResponse = await excursionAPI.patchExcursionAccepted(uid, sid, ex_tid)
        const status = (patchResponse as any)?.data.status

        switch (status) {
            case 'ok':
                return true;
            case 'expired_by_guide':
                toast.error('Экскурсия завершена');
                break
            default:
                toast.error('Что-то пошло не так. Попробуйте еще раз');
                break
        }
    }, [cookies]);

    return {
        getExcursionInfo,
        joinMe,
        startStream,
        handleGrpNew,
    }
}

export const prepareExcursion = (excursion: ExcursionType): ExcursionPrepared => {
    let excursionPrepared = (excursion as ExcursionPrepared);

    excursionPrepared.isOnline = [
        'in_progress',
        // 'grp_wait_confirmation_by_guide',
        'grp_wait_start_by_user',
        'payed'
    ].indexOf(excursion.ex_status) !== -1;

    excursionPrepared.isRoute = excursion.ex_status === 'route_on_map';

    const time = excursion.ex_start_time || excursion.ex_time_start;
    excursionPrepared.start_time = excursionPrepared.isOnline
        ? 'Прямой эфир'
        : formatDate(time);

    if (excursion.ex_cost) {
        excursionPrepared.price_formatted = excursion.ex_cost + ' ₽';
    } else if (excursion.ex_h_cost) {
        excursionPrepared.price_formatted = excursion.ex_h_cost + ' ₽ / час';
    } else {
        excursionPrepared.price_formatted = 'FREE';
    }

    excursionPrepared.ex_name = excursion.ex_name.replaceAll(/(\p{Extended_Pictographic})/ug, ' $1');
    excursionPrepared.ex_description = excursion.ex_description.replaceAll(/(\p{Extended_Pictographic})/ug, ' $1');
    excursionPrepared.ex_description_ext = excursion.ex_description_ext.replaceAll(/(\p{Extended_Pictographic})/ug, ' $1');

    excursionPrepared.firstImageUrl = excursion.ex_images.length > 0
        ? excursion.ex_images[0].url
        : placeholder;

    let url_prefix = 'https://st.2aw.io/';
    if (window.location.host.indexOf('.local') !== -1) {
        url_prefix = 'http://cabinet.2aw.local/';
    } else if (window.location.host.indexOf('localhost') !== -1) {
        url_prefix = 'http://localhost:3001/';
    }

    excursionPrepared.ex_url_formatted = excursion.ex_url.replace('https://st.2aw.io/', url_prefix);

    return excursionPrepared;
}


const pad = (v: number) => v.toString().padStart(2, '0');

const formatDate = (ex_time_start?: string) => {
    if (!ex_time_start) return ''

    const date = new Date(ex_time_start)
    const year = date.getFullYear()
    const month = pad(date.getMonth() + 1)
    const day = pad(date.getDate())
    const hour = pad(date.getHours())
    const minute = pad(date.getMinutes())

    return `${day}.${month}.${year} ${hour}:${minute}`
}

export { useExcursion }
